import React, { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import {
  FaPlay,
  FaPause,
  FaDownload,
  FaChrome,
  FaSpinner,
  FaTimes,
  FaExpand,
  FaCompress,
  FaLink,
  FaClosedCaptioning,
} from "react-icons/fa";
import { RiVideoLine, RiDeleteBin6Line } from "react-icons/ri";
import { medClipExtensionConfig } from "../../utils/authConfig";
import {
  deleteCreVideo,
  getCreVideoDownloadUrl,
} from "../../utils/API_SERVICE_CRE_VIDEO";
import { fetchStudyVideos, resolveShareUrl } from "../../utils/videoService";
import { replaceVideoUrlDomain } from "../../utils/helper";
import { useAuth } from "../../Hooks/useAuth";
import { useStudyId } from "../../Hooks/useStudyId";
import { useMedClipExtension } from "../../Hooks/useMedClipExtension";
import { useTranslation } from "react-i18next";

const PAGE_SIZE = 20;

function formatCreVideoRow(item) {
  const playbackUrl =
    item.status === "completed" && item.longUrl
      ? replaceVideoUrlDomain(item.longUrl)
      : null;
  return {
    id: item.streamId || item.vid,
    streamId: item.streamId,
    vid: item.vid,
    name: item.isIntro ? `Intro Video ${item.vid}` : `Video ${item.vid}`,
    url: playbackUrl,
    captionUrl: item.captionUrl
      ? replaceVideoUrlDomain(item.captionUrl)
      : null,
    shareableUrl: item.shareableUrl || null,
    status: item.status || "pending",
    date: item.createdAt || new Date().toISOString(),
    errorMessage: item.errorMessage || null,
    isIntro: Boolean(item.isIntro),
  };
}

const CreVideoRecording = () => {
  const { t } = useTranslation("medclip");
  const accessToken = useAuth();
  const studyId = useStudyId();

  const [videos, setVideos] = useState([]);
  const [activeTab, setActiveTab] = useState("recorded");
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [playbackUrl, setPlaybackUrl] = useState(null);
  const [captionsEnabled, setCaptionsEnabled] = useState(false);

  const videoRef = useRef(null);
  const processedVidsRef = useRef(new Set());
  const playbackRetryRef = useRef(false);

  const fetchVideoList = useCallback(
    async (page = 0) => {
      if (!accessToken || !studyId) return;
      setIsLoading(true);
      try {
        const response = await fetchStudyVideos(studyId, {
          useCreApi: true,
          pageIndex: page,
          pageSize: PAGE_SIZE,
          // Intro candidates use an unconfirmed heuristic — only on first page.
          includeIntroCandidates: page === 0,
        });
        if (response.success) {
          setVideos(response.payload.map(formatCreVideoRow));
          setHasMore(response.hasMore);
          setPageIndex(page);
        }
      } catch (error) {
        console.error("[MedClip] list videos failed:", error);
        toast.error(t("failedToFetchVideos"));
      } finally {
        setIsLoading(false);
      }
    },
    [accessToken, studyId, t],
  );

  const onRecordingComplete = useCallback(
    (payload) => {
      const vid = payload?.vid;
      const payloadStudyId = payload?.studyId;
      if (studyId && payloadStudyId && payloadStudyId !== studyId) return;
      if (vid && processedVidsRef.current.has(vid)) return;
      if (vid) processedVidsRef.current.add(vid);
      void fetchVideoList(0);
      toast.success(t("recordingComplete") || "Recording uploaded successfully.");
    },
    [studyId, fetchVideoList, t],
  );

  const {
    integrationActive,
    isExtensionInstalled,
    showInstallGuide,
    setShowInstallGuide,
    isExtensionSectionDismissed,
    setIsExtensionSectionDismissed,
    recordingState,
    postToExtension,
    handleStartRecording,
    handleStopRecording,
    handleCancelRecording,
    handleOpenIntroRecorder,
    requestRecordingState,
  } = useMedClipExtension({
    studyId,
    enabled: true,
    onRecordingComplete,
    t,
  });

  useEffect(() => {
    if (accessToken && studyId && integrationActive) {
      void fetchVideoList(0);
      requestRecordingState();
    }
  }, [accessToken, studyId, integrationActive, fetchVideoList, requestRecordingState]);

  const captionUrl = selectedVideo?.captionUrl || null;

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !captionUrl) return;

    const applyCaptionMode = () => {
      for (let i = 0; i < video.textTracks.length; i += 1) {
        const track = video.textTracks[i];
        if (track.kind === "captions") {
          track.mode = captionsEnabled ? "showing" : "hidden";
        }
      }
    };

    applyCaptionMode();
    video.textTracks.addEventListener("addtrack", applyCaptionMode);
    return () => {
      video.textTracks.removeEventListener("addtrack", applyCaptionMode);
    };
  }, [playbackUrl, captionUrl, captionsEnabled]);

  const handleInstallExtension = useCallback(() => {
    const url = medClipExtensionConfig.getExtensionUrl();
    if (!url) {
      toast.info(t("edgeNotAvailable"));
      return;
    }
    window.open(url, "_blank");
    setShowInstallGuide(true);
    toast.info(t("installFromStore", { store: t("chromeWebStore") }));
  }, [setShowInstallGuide, t]);

  const resolvePlaybackForVideo = useCallback(async (video, { forceRefresh = false } = {}) => {
    if (!video) return null;
    if (video.status !== "completed") return null;
    if (video.url && !forceRefresh) return video.url;
    if (!video.streamId) return null;
    try {
      const res = await getCreVideoDownloadUrl(video.streamId);
      const url = res?.payload?.url;
      return url ? replaceVideoUrlDomain(url) : null;
    } catch (err) {
      console.error("[MedClip] download URL failed:", err);
      return null;
    }
  }, []);

  const handlePlaybackError = useCallback(async () => {
    if (!selectedVideo || playbackRetryRef.current) return;
    playbackRetryRef.current = true;
    const freshUrl = await resolvePlaybackForVideo(selectedVideo, { forceRefresh: true });
    if (freshUrl) {
      setPlaybackUrl(freshUrl);
    } else {
      toast.error(t("playbackNotReady"));
    }
    playbackRetryRef.current = false;
  }, [selectedVideo, resolvePlaybackForVideo, t]);

  const handleVideoSelect = useCallback(
    async (video) => {
      if (videoRef.current && !videoRef.current.paused) {
        videoRef.current.pause().catch(() => {});
      }
      setSelectedVideo(video);
      setIsPlaying(false);
      setCurrentTime(0);
      setPlaybackUrl(null);
      setCaptionsEnabled(Boolean(video.captionUrl));
      playbackRetryRef.current = false;

      if (video.status === "failed") {
        console.warn("[MedClip] failed video:", video.errorMessage);
        return;
      }
      if (video.status !== "completed") {
        return;
      }

      const url = await resolvePlaybackForVideo(video);
      setPlaybackUrl(url);
      if (!url) {
        toast.info(t("playbackNotReady"));
      }
    },
    [resolvePlaybackForVideo, t],
  );

  const handleDeleteVideo = useCallback(
    (videoId) => {
      const videoToDelete = videos.find((v) => v.id === videoId);
      if (!videoToDelete?.streamId) {
        toast.error(t("videoNotFound"));
        return;
      }

      setVideos((prev) => prev.filter((v) => v.id !== videoId));
      if (selectedVideo?.id === videoId) {
        setSelectedVideo(null);
        setPlaybackUrl(null);
        setIsPlaying(false);
      }
      toast.success(t("videoDeletedSuccessfully"));

      deleteCreVideo(videoToDelete.streamId).catch((error) => {
        console.error("[MedClip] delete failed:", error);
        toast.error(t("failedToDeleteVideoOnServer"));
        setVideos((prev) => {
          if (prev.some((v) => v.id === videoId)) return prev;
          return [...prev, videoToDelete];
        });
      });
    },
    [videos, selectedVideo, t],
  );

  const handleDownload = useCallback(
    async (video) => {
      const url = await resolvePlaybackForVideo(video);
      if (!url) {
        toast.error(t("playbackNotReady"));
        return;
      }
      const link = document.createElement("a");
      link.href = url;
      link.download = video.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    [resolvePlaybackForVideo, t],
  );

  const handleCopyVideoLink = useCallback(
    async (video) => {
      const shareUrl = resolveShareUrl(
        { shareableUrl: video.shareableUrl, vid: video.vid },
        { useCreApi: true },
      );
      if (!shareUrl) {
        toast.error(t("shareLinkUnavailable"));
        return;
      }
      try {
        await navigator.clipboard.writeText(shareUrl);
      } catch {
        const textArea = document.createElement("textarea");
        textArea.value = shareUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      toast.success(t("videoLinkCopiedToClipboard"));
    },
    [t],
  );

  const handlePlayPause = useCallback(async () => {
    if (!videoRef.current) return;
    try {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        await videoRef.current.play();
        setIsPlaying(true);
      }
    } catch (error) {
      if (error.name !== "NotAllowedError" && error.name !== "AbortError") {
        console.error("play/pause error:", error);
      }
      setIsPlaying(!videoRef.current.paused);
    }
  }, [isPlaying]);

  const handleToggleCaptions = useCallback(() => {
    setCaptionsEnabled((prev) => !prev);
  }, []);

  const formatTime = useCallback((time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }, []);

  if (!integrationActive) {
    return null;
  }

  const filteredVideos = videos.filter((video) =>
    activeTab === "intro" ? video.isIntro : !video.isIntro,
  );

  const statusLabel = (status) => {
    if (status === "completed") return `✅ ${t("completed")}`;
    if (status === "processing") return t("processing");
    if (status === "pending") return t("pending");
    if (status === "failed") return t("failed");
    return status;
  };

  return (
    <div className="mt-6 p-6 border dark:border-gray-700 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {t("videoRecordingTitle")}
            </h2>
            <p className="text-gray-600 dark:text-gray-300">
              {t("videoRecordingDescription")}
            </p>
          </div>
          {isLoading && (
            <FaSpinner className="animate-spin text-[#A4C639] dark:text-[#CBEA7B]" />
          )}
        </div>
      </div>

      {!isExtensionInstalled && !isExtensionSectionDismissed && (
        <div className="mb-6 p-4 bg-gray-50 dark:bg-[#111827] rounded-lg border-2 border-gray-200 dark:border-[#111827] relative shadow-sm">
          <button
            onClick={() => setIsExtensionSectionDismissed(true)}
            className="absolute top-2 right-2 p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            title={t("dismiss")}
          >
            <FaTimes className="text-sm" />
          </button>
          <div className="flex items-center justify-between pr-8">
            <div className="flex items-center space-x-3">
              <FaChrome className="text-gray-600 dark:text-white text-2xl" />
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  {t("extensionTitle")}
                </h3>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {t("extensionDescription")}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {t("extensionLoggedIn")}{" "}
                  <button
                    onClick={() => postToExtension("project.get")}
                    className="ml-2 text-blue-600 hover:underline dark:text-blue-400"
                  >
                    {t("refreshStatus")}
                  </button>
                </p>
              </div>
            </div>
            <button
              onClick={handleInstallExtension}
              className="px-4 py-2 bg-[#A4C639] dark:bg-[#A4C639] text-white rounded-lg hover:bg-[#CBEA7B80] transition-colors"
            >
              {t("installExtension")}
            </button>
          </div>
          {showInstallGuide && (
            <div className="mt-4 p-3 bg-gray-100 dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-600">
              <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                {t("installGuideTitle")}
              </h4>
              <ol className="list-decimal list-inside text-sm text-gray-800 dark:text-gray-300 space-y-1">
                <li>{t("installStep1")}</li>
                <li>{t("installStep2Chrome")}</li>
                <li>{t("installStep3")}</li>
                <li>{t("installStep4")}</li>
              </ol>
              <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                {t("edgeNotAvailable")}
              </p>
            </div>
          )}
        </div>
      )}

      {isExtensionInstalled && (
        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={
                recordingState.isRecording
                  ? handleStopRecording
                  : handleStartRecording
              }
              className={`px-4 py-2 rounded-lg text-white transition-colors ${
                recordingState.isRecording
                  ? "bg-red-600 hover:bg-red-700"
                  : "bg-[#A4C639] hover:bg-[#CBEA7B80] text-gray-900"
              }`}
              disabled={
                recordingState.isStarting ||
                recordingState.isProcessing ||
                recordingState.isLoading
              }
            >
              {recordingState.isStarting
                ? t("starting")
                : recordingState.isLoading
                  ? t("stopping")
                  : recordingState.isProcessing
                    ? t("processing")
                    : recordingState.isRecording
                      ? t("stopRecording")
                      : t("launchRecording")}
            </button>
            {(recordingState.isRecording || recordingState.isPaused) && (
              <button
                onClick={handleCancelRecording}
                className="px-4 py-2 rounded-lg bg-gray-300 hover:bg-gray-400 text-gray-900 dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-white"
              >
                {t("cancelRecording")}
              </button>
            )}
            <button
              onClick={handleOpenIntroRecorder}
              className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-900 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white"
            >
              {t("openIntroRecorder")}
            </button>
            <div className="text-sm text-gray-700 dark:text-gray-300">
              {recordingState.isRecording
                ? t("recordingInProgress")
                : recordingState.isProcessing
                  ? t("processingRecording")
                  : t("ready")}
            </div>
          </div>
          {recordingState.error && (
            <div className="mt-2 text-sm text-red-600 dark:text-red-400">
              {recordingState.error}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="flex items-center gap-2 mb-4 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setActiveTab("recorded")}
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === "recorded"
                  ? "text-[#A4C639] border-b-2 border-[#A4C639]"
                  : "text-gray-600 dark:text-gray-400"
              }`}
            >
              {t("recordedVideos")} ({videos.filter((v) => !v.isIntro).length})
            </button>
            <button
              onClick={() => setActiveTab("intro")}
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === "intro"
                  ? "text-[#A4C639] border-b-2 border-[#A4C639]"
                  : "text-gray-600 dark:text-gray-400"
              }`}
            >
              {t("introVideos")} ({videos.filter((v) => v.isIntro).length})
            </button>
          </div>

          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredVideos.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <RiVideoLine className="mx-auto text-4xl mb-2" />
                <p>
                  {activeTab === "intro"
                    ? t("noIntroVideosYet")
                    : t("noRecordedVideosYet")}
                </p>
              </div>
            ) : (
              filteredVideos.map((video) => (
                <div
                  key={video.id}
                  className={`p-3 rounded-lg border cursor-pointer ${
                    selectedVideo?.id === video.id
                      ? "bg-[#CBEA7B] dark:bg-[#A4C63980] border-[#A4C639]"
                      : "bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600"
                  }`}
                  onClick={() => void handleVideoSelect(video)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{video.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {statusLabel(video.status)} •{" "}
                        {new Date(video.date).toLocaleDateString()}
                      </p>
                      {video.status === "failed" && video.errorMessage && (
                        <p className="text-xs text-red-500 truncate" title={video.errorMessage}>
                          {video.errorMessage}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center space-x-1 ml-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleDownload(video);
                        }}
                        className="p-1 text-gray-500 hover:text-[#A4C639]"
                        title={t("download")}
                        disabled={video.status !== "completed"}
                      >
                        <FaDownload className="text-sm" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleCopyVideoLink(video);
                        }}
                        className="p-1 text-gray-500 hover:text-[#A4C639]"
                        title={t("copyVideoLink")}
                        disabled={!video.vid}
                      >
                        <FaLink className="text-sm" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteVideo(video.id);
                        }}
                        className="p-1 text-gray-500 hover:text-red-600"
                        title={t("delete")}
                      >
                        <RiDeleteBin6Line className="text-sm" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="flex items-center justify-between mt-3 text-sm">
            <button
              type="button"
              disabled={pageIndex <= 0 || isLoading}
              onClick={() => void fetchVideoList(pageIndex - 1)}
              className="px-2 py-1 rounded border disabled:opacity-50"
            >
              {t("previousPage")}
            </button>
            <span className="text-gray-500">{t("page")} {pageIndex + 1}</span>
            <button
              type="button"
              disabled={!hasMore || isLoading}
              onClick={() => void fetchVideoList(pageIndex + 1)}
              className="px-2 py-1 rounded border disabled:opacity-50"
            >
              {t("nextPage")}
            </button>
          </div>
        </div>

        <div className="lg:col-span-2">
          {selectedVideo && playbackUrl ? (
            <div className="bg-black rounded-lg overflow-hidden">
              <video
                ref={videoRef}
                src={playbackUrl}
                crossOrigin={captionUrl ? "anonymous" : undefined}
                className="w-full h-64 lg:h-80 object-contain"
                onTimeUpdate={() =>
                  setCurrentTime(videoRef.current?.currentTime || 0)
                }
                onLoadedMetadata={() => {
                  setDuration(videoRef.current?.duration || 0);
                  setIsPlaying(!videoRef.current?.paused);
                }}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onError={() => void handlePlaybackError()}
              >
                {captionUrl ? (
                  <track
                    key={captionUrl}
                    kind="captions"
                    src={captionUrl}
                    srcLang="en"
                    label="English"
                    default
                  />
                ) : null}
              </video>
              <div className="p-4 bg-gray-900 text-white">
                <div className="flex items-center gap-4 mb-3">
                  <button
                    onClick={() => void handlePlayPause()}
                    className="w-10 h-10 bg-[#CBEA7B] rounded-full flex items-center justify-center shrink-0"
                  >
                    {isPlaying ? <FaPause /> : <FaPlay />}
                  </button>
                  <span className="text-sm text-gray-300">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </span>
                  {captionUrl ? (
                    <button
                      type="button"
                      onClick={handleToggleCaptions}
                      className={`ml-auto w-10 h-10 rounded-full flex items-center justify-center transition-colors shrink-0 ${
                        captionsEnabled
                          ? "bg-white text-gray-900 hover:bg-gray-100"
                          : "bg-gray-700 text-gray-400 hover:text-gray-200 hover:bg-gray-600"
                      }`}
                      title={
                        captionsEnabled
                          ? t("captionsOn")
                          : t("captionsOff")
                      }
                      aria-pressed={captionsEnabled}
                      aria-label={t("toggleCaptions")}
                    >
                      <FaClosedCaptioning />
                    </button>
                  ) : null}
                </div>
                <div className="flex items-center justify-between">
                  <h4 className="font-medium truncate">{selectedVideo.name}</h4>
                  <button
                    onClick={() => {
                      if (!document.fullscreenElement) {
                        videoRef.current?.requestFullscreen();
                      } else {
                        document.exitFullscreen();
                      }
                    }}
                    className="text-gray-300 hover:text-[#CBEA7B]"
                  >
                    {document.fullscreenElement ? <FaCompress /> : <FaExpand />}
                  </button>
                </div>
              </div>
            </div>
          ) : selectedVideo ? (
            <div className="flex items-center justify-center h-64 bg-gray-100 dark:bg-gray-700 rounded-lg text-gray-500">
              {selectedVideo.status === "failed"
                ? selectedVideo.errorMessage || t("failed")
                : t("playbackNotReady")}
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 bg-gray-100 dark:bg-gray-700 rounded-lg text-gray-500">
              {t("selectVideoToPlay")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreVideoRecording;
